/**
 * costplan-pivot-shared — types + small formatters for the
 * `costplan_pivot` strategy response, shared between every card that
 * reads it: `ObeyaCostPlanLinesCard` (full editor, group/expand/edit)
 * and `ObeyaCostPlanRequirementsCard` (read-only rail summary). One
 * payload shape, two renderers with different capability — see each
 * card's own docstring for why they're separate components rather than
 * one gated by a prop.
 *
 * @domain Canvas / Obeya
 * @layer presentation
 */

export type GroupByKey = 'line' | 'slot' | 'vendor';
export const UNASSIGNED_KEY = '__unassigned__';
// Line mode's _pivot_key fallback (costplan_pivot.py) for a line with no
// traceable schedule requirement — keeps its own per-line group but has no
// real source_line_sid to focus, so click-to-focus excludes groups keyed
// this way.
export const NO_SOURCE_LINE_PREFIX = 'no_source_line:';

export interface PivotRow {
  line_id: string | null;
  product_sid: string | null;
  product_name: string | null;
  product_sku: string | null;
  vendor_id: string | null;
  vendor_name: string | null;
  slot_sid: string | null;
  slot_name: string | null;
  slot_count: number | null;
  qty: number;
  value: number;
  // The plan's own per-unit price (ValueState.unit_cost) — distinct from
  // quoted_unit_price (a vendor's RFQ quote). Line mode's inline price
  // editor edits this; other modes ignore it.
  unit_price: number;
  // Buyer-typed spec text ("Upholstery: Mokum / Verona — Oat").
  spec: string | null;
  reaches_rfq: boolean;
  fully_allocated: boolean;
  reaches_quote: boolean;
  quoted_unit_price: number | null;
  quoted_by_vendor: string | null;
  // Set only on the synthetic Unassigned/remainder row, when placements
  // sum past the line's own quantity — that row's `qty` goes negative
  // (real over-allocation amount) instead of being clamped away.
  over_allocated?: boolean;
  // The schedule swimlane this candidate was picked to satisfy, when it
  // came from Discovery — label resolved live server-side, never a
  // snapshot. Null for lines added by plain catalogue browsing.
  source_line_sid?: string | null;
  source_line_label?: string | null;
}

export interface QuoteSummary {
  reaches_quote: boolean;
  quoted_unit_price: number | null;
  quoted_by_vendor: string | null;
  quoted_product_count: number | null;
}

export interface VendorColumn {
  vendor_sid: string;
  vendor_name: string;
}

export interface PivotGroup {
  key: string;
  label: string;
  rows: PivotRow[];
  // Line mode only: null (not 0) when the group's competing lines
  // disagree on quantity — see qty_mismatch. Slot/Vendor mode always
  // has a real number (a legitimate sum across distinct products).
  total_qty: number | null;
  // Line mode only, when the group spans >1 product: several
  // competing lines disagreeing on how much of the requirement is
  // needed — a real discrepancy, not resolved to one guessed number.
  qty_mismatch?: boolean;
  total_value: number;
  // Line mode only — cheapest to priciest competing line's own
  // (quantity × unit price), never a sum across alternatives as if
  // buying all of them. null for Slot/Vendor mode groups, which don't
  // have this "several prices for one demand" shape at all.
  value_low?: number | null;
  value_high?: number | null;
  quote_summary: QuoteSummary;
  // Line mode only — one quote per vendor for this group's (single)
  // product, keyed by vendor_sid.
  quotes_by_vendor: Record<string, number | null>;
  // Distinct products in the group — Vendor/Area mode's "N lines"
  // subtitle. BFF-computed.
  product_count: number;
  // Set server-side (build_costplan_pivot) only on the first group of
  // a products/services split, and only when the plan actually has
  // both — null on every group when it doesn't, same order as today.
  // Render a section header exactly where this is set, nowhere else.
  section_label?: string | null;
}

export interface CostPlanPivotData {
  groups: PivotGroup[];
  group_count: number;
  total_value: number;
  // Line mode only — plan-wide value range (see PivotGroup.value_low/
  // value_high, summed once more). Absent for Slot/Vendor mode, which
  // keep the single `total_value` sum above (legitimate there).
  total_value_low?: number | null;
  total_value_high?: number | null;
  // Non-empty only in Line mode — a vendor-per-column comparison only
  // means one thing when a group is exactly one product.
  vendor_columns: VendorColumn[];
  labels?: Record<string, string>;
}

export interface RoomSlotOption { sid: string; name: string; }
export interface RoomProgrammeData {
  room_types: { sid: string; name: string }[];
  specific_rooms: { sid: string; name: string }[];
}

export function money(v: number): string {
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

// Formatting only — the decision of WHAT to show (price vs. a distinct-
// product count) was made server-side in build_costplan_pivot's
// quote_summary; this just paints the string.
export function quoteCell(qs: QuoteSummary, L: Record<string, string>): string {
  if (!qs.reaches_quote) return '—';
  if (qs.quoted_unit_price != null) {
    return `${money(qs.quoted_unit_price)}${qs.quoted_by_vendor ? ` · ${qs.quoted_by_vendor}` : ''}`;
  }
  if (qs.quoted_product_count) {
    return `${qs.quoted_product_count} ${L.col_quoted ?? ''}`.trim();
  }
  return '—';
}

export function leafQuoteCell(r: PivotRow): string {
  if (!r.reaches_quote || r.quoted_unit_price == null) return '—';
  return `${money(r.quoted_unit_price)}${r.quoted_by_vendor ? ` · ${r.quoted_by_vendor}` : ''}`;
}
